import { useState } from "react";
import type { SleevePerformance, Algo } from "../api/types";
import { usd } from "../lib/format";

type SortKey = "preset" | "pnl_total" | "pnl_today" | "sharpe" | "profit_factor" | "win_rate" | "max_consecutive_losses" | /* "cagr" | */ "trade_count" | "drawdown" | "time_in_sim_days";
type SortDir = "asc" | "desc";

const CRYPTO_MARKETS = new Set(["btc", "eth", "sol", "xbt", "xet"]);

function assetClass(market: string, preset: string): "crypto" | "futures" {
  const m = market.toLowerCase();
  if (CRYPTO_MARKETS.has(m) || preset.toLowerCase().startsWith("carry_") || preset.toLowerCase().startsWith("funding_mr_")) return "crypto";
  return "futures";
}

function shortMarketClass(market: string): string {
  const m = market.toUpperCase();
  if (m === "BTCUSDT" || m === "ETHUSDT") return m.replace("USDT", "");
  return m;
}

interface Props {
  sleeves: SleevePerformance[];
  algos: Algo[];
}

export function SleevePerformanceMatrix({ sleeves, algos }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("pnl_total");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  }
  function sortArrow(k: SortKey): string {
    return sortKey === k ? (sortDir === "desc" ? " \u25bc" : " \u25b2") : "";
  }

  const algoMap = new Map(algos.map((a) => [a.id, a]));

  const merged = sleeves.map((s) => ({
    ...s,
    algo: algoMap.get(s.algo_id),
    class: assetClass(s.market, s.preset),
  }));

  let filtered = merged;

  const sorted = [...filtered].sort((a, b) => {
    let va: number | string, vb: number | string;
    switch (sortKey) {
      case "preset": va = a.preset; vb = b.preset; break;
      case "pnl_total": va = a.pnl_total; vb = b.pnl_total; break;
      case "pnl_today": va = a.pnl_today; vb = b.pnl_today; break;
      case "sharpe": va = a.sharpe; vb = b.sharpe; break;
      case "profit_factor": va = a.profit_factor ?? 0; vb = b.profit_factor ?? 0; break;
      case "win_rate": va = a.win_rate; vb = b.win_rate; break;
      case "max_consecutive_losses": va = a.max_consecutive_losses ?? 0; vb = b.max_consecutive_losses ?? 0; break;
      // case "cagr": va = a.cagr ?? 0; vb = b.cagr ?? 0; break;
      case "drawdown": va = a.drawdown; vb = b.drawdown; break;
      case "time_in_sim_days": va = a.time_in_sim_days ?? 0; vb = b.time_in_sim_days ?? 0; break;
      default:
        va = a.pnl_total; vb = b.pnl_total;
    }
    const cmp = typeof va === "string" ? va.localeCompare(vb as string) : (va as number) - (vb as number);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const futuresRows = sorted.filter((s) => s.class === "futures");
  const cryptoRows = sorted.filter((s) => s.class === "crypto");

  const colCount = 13;

  return (
    <div className="card">
      <h2>Sleeve Matrix</h2>
      {sorted.length === 0 ? (
        <div className="muted small">no sleeve data</div>
      ) : (
        <div className="table-wrap" style={{ flex: 1, overflow: "auto" }}>
          <table className="pcc-matrix">
            <thead>
              <tr>
                <th className="sortable" onClick={() => toggleSort("preset")}>Sleeve{sortArrow("preset")}</th>
                <th className="num sortable" onClick={() => toggleSort("pnl_total")}>Total P&L{sortArrow("pnl_total")}</th>
                <th className="num sortable" onClick={() => toggleSort("pnl_today")}>Today{sortArrow("pnl_today")}</th>
                <th className="num sortable" onClick={() => toggleSort("win_rate")}>Win%{sortArrow("win_rate")}</th>
                {/* <th className="num sortable" onClick={() => toggleSort("max_consecutive_losses")}>MaxL{sortArrow("max_consecutive_losses")}</th> */}
                <th className="num sortable" onClick={() => toggleSort("sharpe")}>Sharpe{sortArrow("sharpe")}</th>
                <th className="num sortable" onClick={() => toggleSort("profit_factor")}>PF{sortArrow("profit_factor")}</th>
                {/* <th className="num sortable" onClick={() => toggleSort("cagr")}>CAGR{sortArrow("cagr")}</th> */}
                <th className="num sortable" onClick={() => toggleSort("drawdown")}>DD{sortArrow("drawdown")}</th>
                <th className="num sortable" onClick={() => toggleSort("trade_count")}>Trades{sortArrow("trade_count")}</th>
                <th className="num sortable" onClick={() => toggleSort("time_in_sim_days")}>Days{sortArrow("time_in_sim_days")}</th>
                <th>Gate</th>
                {/* <th className="num">DD/PnL</th> */}
              </tr>
            </thead>
            <tbody>
              {futuresRows.length > 0 && (
                <>
                  <tr className="section-row">
                    <td colSpan={colCount}>
                      <span className="pill dim">FUTURES</span>
                    </td>
                  </tr>
                  {futuresRows.map((s) => <Row key={s.algo_id} s={s} />)}
                </>
              )}
              {cryptoRows.length > 0 && (
                <>
                  <tr className="section-row">
                    <td colSpan={colCount}>
                      <span className="pill kind">CRYPTO</span>
                    </td>
                  </tr>
                  {cryptoRows.map((s) => <Row key={s.algo_id} s={s} />)}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ s }: { s: SleevePerformance & { algo?: Algo; class: "crypto" | "futures" } }) {
  const promo = promotionGate(s);
  return (
    <tr className={s.class === "crypto" ? "crypto-row" : undefined}>
      <td className="pcc-matrix-preset">
        <span className="mono">{s.preset}</span>
        <span className="pill dim" style={{ marginLeft: 6 }}>{shortMarketClass(s.market)}</span>
      </td>
      <td className={"num " + pnlCls(s.pnl_total)}>{usd(s.pnl_total, { sign: true })}</td>
      <td className={"num " + pnlCls(s.pnl_today)}>{usd(s.pnl_today, { sign: true })}</td>
      <td className="num">{pct(s.win_rate)}</td>
      {/* <td className="num">{s.max_consecutive_losses != null ? s.max_consecutive_losses : "—"}</td> */}
      <td className={"num " + metricCls(s.sharpe, 1.0, 0.5)}>{s.sharpe.toFixed(2)}</td>
      <td className={"num " + metricCls(s.profit_factor ?? 0, 1.5, 1.0)}>{s.profit_factor != null ? s.profit_factor.toFixed(2) : "—"}</td>
      {/* <td className="num">{s.cagr != null ? (s.cagr * 100).toFixed(2) + "%" : "—"}</td> */}
      <td className="num bad">{usd(s.drawdown)}</td>
      <td className="num">{s.trade_count}</td>
      <td className="num">{s.time_in_sim_days != null ? s.time_in_sim_days : "—"}</td>
      <td>
        {promo.ready ? (
          <span className="pill good">READY</span>
        ) : (
          <span className="pill warn" title={promo.gap}>{promo.short}</span>
        )}
      </td>
      {/* <td className={`num ${riskPct !== null ? (riskPct < 20 ? "desk-ticker-ok" : riskPct < 50 ? "warn" : "desk-ticker-bad") : ""}`}>
        {riskPct !== null ? riskPct.toFixed(0) + "%" : "—"}
      </td> */}
    </tr>
  );
}

function promotionGate(s: SleevePerformance): { ready: boolean; gap: string; short: string } {
  if ((s.time_in_sim_days ?? 0) < 30) {
    return { ready: false, gap: `Need 30 days in sim; currently ${s.time_in_sim_days ?? 0}`, short: "sim < 30d" };
  }
  if (s.trade_count < 30) {
    return { ready: false, gap: `Need 30+ trades; currently ${s.trade_count}`, short: "n < 30" };
  }
  const misses: string[] = [];
  if ((s.profit_factor ?? 0) < 1.0) misses.push(`PF ${(s.profit_factor ?? 0).toFixed(2)} < 1.0`);
  if (s.sharpe < 0.5) misses.push(`Sharpe ${s.sharpe.toFixed(2)} < 0.5`);
  if (misses.length === 0) return { ready: true, gap: "", short: "READY" };
  return { ready: false, gap: misses.join("; "), short: "gate" };
}

function pnlCls(n: number): string {
  return n > 0 ? "good" : n < 0 ? "bad" : "";
}

function pct(n: number): string {
  return (n * 100).toFixed(0) + "%";
}

function metricCls(v: number, good: number, warn: number): string {
  return v >= good ? "good" : v >= warn ? "warn" : "bad";
}
